/**
 * TOTP (RFC 6238) client-side.
 *
 * Folosim WebCrypto SubtleCrypto pentru HMAC — implementările native sunt
 * auditate și nu introducem dependențe suplimentare (respectăm CSP strict).
 *
 * Default-urile match-uiesc Google Authenticator / Authy / 1Password:
 *   - algorithm: SHA-1
 *   - digits: 6
 *   - period: 30s
 *
 * Parser `otpauth://` conform Key URI Format:
 * https://github.com/google/google-authenticator/wiki/Key-Uri-Format
 */
import i18n from '@/i18n';

export type TotpAlgorithm = 'SHA1' | 'SHA256' | 'SHA512';

export interface TotpConfig {
  secret: string; // base32, fără padding strict (acceptăm input lax)
  digits?: number;
  period?: number;
  algorithm?: TotpAlgorithm;
}

export interface ParsedOtpauth {
  issuer: string | null;
  account: string;
  secret: string;
  algorithm: TotpAlgorithm;
  digits: number;
  period: number;
}

export const TOTP_DEFAULTS = {
  digits: 6,
  period: 30,
  algorithm: 'SHA1' as TotpAlgorithm,
};

// ============================================================================
// Base32 decode (RFC 4648). Acceptă lowercase și ignore-uie padding + spații.
// ============================================================================

const B32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function base32Decode(input: string): Uint8Array {
  const cleaned = input.replace(/[\s-]/g, '').replace(/=+$/, '').toUpperCase();
  if (!/^[A-Z2-7]*$/.test(cleaned)) {
    throw new Error('Base32 invalid: caractere în afara alfabetului RFC 4648');
  }
  const bits: number[] = [];
  for (const ch of cleaned) {
    const idx = B32_ALPHABET.indexOf(ch);
    for (let i = 4; i >= 0; i--) bits.push((idx >> i) & 1);
  }
  const byteCount = Math.floor(bits.length / 8);
  const out = new Uint8Array(byteCount);
  for (let i = 0; i < byteCount; i++) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i * 8 + j];
    out[i] = b;
  }
  return out;
}

export function isValidBase32(input: string): boolean {
  try {
    const bytes = base32Decode(input);
    return bytes.length > 0;
  } catch {
    return false;
  }
}

// ============================================================================
// HMAC-based one-time password (HOTP, RFC 4226) — block pe counter 8 bytes.
// ============================================================================

async function hmacSha(
  key: Uint8Array,
  message: Uint8Array,
  algorithm: TotpAlgorithm,
): Promise<Uint8Array> {
  const algoName = {
    SHA1: 'SHA-1',
    SHA256: 'SHA-256',
    SHA512: 'SHA-512',
  }[algorithm];
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: { name: algoName } },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, message as BufferSource);
  return new Uint8Array(sig);
}

function counterToBytes(counter: number): Uint8Array {
  const buf = new Uint8Array(8);
  // JS bitwise e 32-bit; split în high/low.
  const high = Math.floor(counter / 0x100000000);
  const low = counter >>> 0;
  new DataView(buf.buffer).setUint32(0, high);
  new DataView(buf.buffer).setUint32(4, low);
  return buf;
}

export async function hotp(
  secret: Uint8Array,
  counter: number,
  digits: number,
  algorithm: TotpAlgorithm,
): Promise<string> {
  const mac = await hmacSha(secret, counterToBytes(counter), algorithm);
  // Dynamic truncation (RFC 4226 §5.3)
  const offset = mac[mac.length - 1] & 0x0f;
  const binCode =
    ((mac[offset] & 0x7f) << 24) |
    ((mac[offset + 1] & 0xff) << 16) |
    ((mac[offset + 2] & 0xff) << 8) |
    (mac[offset + 3] & 0xff);
  const code = binCode % 10 ** digits;
  return code.toString().padStart(digits, '0');
}

// ============================================================================
// TOTP (RFC 6238)
// ============================================================================

export async function computeTotp(
  config: TotpConfig,
  nowMs: number = Date.now(),
): Promise<string> {
  const digits = config.digits ?? TOTP_DEFAULTS.digits;
  const period = config.period ?? TOTP_DEFAULTS.period;
  const algorithm = config.algorithm ?? TOTP_DEFAULTS.algorithm;
  const secretBytes = base32Decode(config.secret);
  const counter = Math.floor(nowMs / 1000 / period);
  return hotp(secretBytes, counter, digits, algorithm);
}

/** Câte ms mai sunt până la următorul code (0...period*1000). */
export function totpMsUntilNext(
  period: number = TOTP_DEFAULTS.period,
  nowMs: number = Date.now(),
): number {
  const cycleMs = period * 1000;
  return cycleMs - (nowMs % cycleMs);
}

// ============================================================================
// otpauth:// parser
// ============================================================================

export function parseOtpauthUrl(raw: string): ParsedOtpauth {
  const url = new URL(raw.trim());
  if (url.protocol !== 'otpauth:') {
    throw new Error(i18n.t('admin_ui.totp.not_otpauth'));
  }
  // URL API tratează host-ul ca lowercase; Key URI folosește "totp" sau "hotp"
  if (url.host.toLowerCase() !== 'totp') {
    throw new Error(i18n.t('admin_ui.totp.only_totp'));
  }
  // Path-ul începe cu '/' și poate conține 'Issuer:Account' sau 'Account'
  const label = decodeURIComponent(url.pathname.replace(/^\//, ''));
  let issuer: string | null = null;
  let account = label;
  const colonIdx = label.indexOf(':');
  if (colonIdx >= 0) {
    issuer = label.slice(0, colonIdx).trim();
    account = label.slice(colonIdx + 1).trim();
  }

  const params = url.searchParams;
  const secret = (params.get('secret') ?? '').trim();
  if (!secret) {
    throw new Error(i18n.t('admin_ui.totp.missing_secret'));
  }
  if (!isValidBase32(secret)) {
    throw new Error(i18n.t('admin_ui.totp.secret_invalid'));
  }

  const queryIssuer = params.get('issuer');
  if (queryIssuer && !issuer) issuer = queryIssuer.trim();
  // Dacă ambele există și diferă, preferăm label-ul (practică comună).

  const algorithmRaw = (params.get('algorithm') ?? TOTP_DEFAULTS.algorithm).toUpperCase();
  if (!['SHA1', 'SHA256', 'SHA512'].includes(algorithmRaw)) {
    throw new Error(i18n.t('admin_ui.totp.unsupported_algorithm', { algorithm: algorithmRaw }));
  }

  const digits = parseInt(params.get('digits') ?? String(TOTP_DEFAULTS.digits), 10);
  if (!Number.isFinite(digits) || digits < 6 || digits > 10) {
    throw new Error(i18n.t('admin_ui.totp.digits_range'));
  }

  const period = parseInt(params.get('period') ?? String(TOTP_DEFAULTS.period), 10);
  if (!Number.isFinite(period) || period < 15 || period > 120) {
    throw new Error(i18n.t('admin_ui.totp.period_range'));
  }

  return {
    issuer,
    account,
    secret,
    algorithm: algorithmRaw as TotpAlgorithm,
    digits,
    period,
  };
}
